// pages/Orders/AllOrders.js
import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import DeleteModal from "pages/Calendar/DeleteModal";
import { orderApiService } from "services/orderApiService";
import OrderDetailsModal from "components/Modals/OrderDetailsModal";
import UpdateOrderStatusModal from "components/Modals/UpdateOrderStatusModal";
import OrderStatusBadge from "components/Common/OrderStatusBadge";
import { generateOrderInvoicePDF } from "utils/invoicePdfGenerator";
import { Link } from "react-router-dom";

const AllOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deletingOrder, setDeletingOrder] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");

  // Fetch orders
  const fetchOrders = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await orderApiService.getAllOrders();
      setOrders(data);
    } catch (error) {
      toast.error("Failed to fetch orders");
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const showDeleteModal = (id) => {
    setSelectedOrderId(id);
    setDeleteModal(true);
  };

  const handleDeleteOrder = async () => {
    setDeletingOrder(true);
    try {
      await orderApiService.deleteOrder(selectedOrderId);
      toast.success("Order deleted successfully");
      setDeleteModal(false);
      fetchOrders(false); // Refresh without showing loader
    } catch (error) {
      toast.error("Failed to delete order");
      console.error("Error deleting order:", error);
    } finally {
      setDeletingOrder(false);
    }
  };

  const handleShowDetails = (order) => {
    setSelectedOrder(order);
    setDetailsModalOpen(true);
  };

  const handleUpdateStatus = (order) => {
    setSelectedOrder(order);
    setStatusModalOpen(true);
  };

  const handleStatusUpdateSuccess = () => {
    fetchOrders(false); // Refresh orders after status update
    toast.success("Order status updated successfully");
  };

  // Filter orders
  const filteredOrders = useMemo(() => {
    if (!orders.length) return [];

    return orders.filter(order => {
      const statusMatch = !statusFilter || order.orderstatus === statusFilter;
      const paymentMatch = !paymentFilter || order.paymentstatus === paymentFilter;

      return statusMatch && paymentMatch;
    });
  }, [orders, statusFilter, paymentFilter]);

  const formatCurrency = (amount) => {
    return `৳${parseFloat(amount || 0).toLocaleString('en-BD')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return dateString;
  };

  const getPaymentMethodBadge = (method) => {
    const methods = {
      Cash_on_delivery: { color: "success", text: "COD" },
      online_payment: { color: "primary", text: "Online" },
      card: { color: "info", text: "Card" },
    };
    const config = methods[method] || { color: "secondary", text: method };
    return <span className={`badge bg-${config.color}`}>{config.text}</span>;
  };

  const columns = useMemo(
    () => [
      {
        header: "Order ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="">#00695{row.original.id}</div>
            <small className="text-muted">
              {formatDate(row.original.orderdate)}
              {/* {row.original.ordertime} */}
            </small>
          </div>
        ),
      },
      {
        header: "Name",
        accessorKey: "username",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="">{row.original.username}</div>
          </div>
        ),
      },
      {
        header: "Phone",
        accessorKey: "phonenumber",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="">{row.original.phonenumber}</div>
          </div>
        ),
      },
      {
        header: "Products",
        accessorKey: "orderproductdata",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const products = row.original.orderproductdata || [];
          return (
            <div>
              {products.slice(0, 3).map((product, index) => (
                <div key={index} className="mb-1">
                  <small>
                    {index + 1}. {product.tytle} {product?.power != "none" ? product?.power : ""} <strong className="text-primary">x{product.quantity}</strong>
                    {/* <br /> */}
                    {/* {product?.type} - {product?.ptype != "none" ? product?.ptype : ""} */}
                  </small>
                </div>
              ))}
              {products.length > 3 && (
                <small className="text-muted">
                  +{products.length - 3} more items
                </small>
              )}
            </div>
          );
        },
      },
      {
        header: "Prescription",
        accessorKey: "image",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const imageUrl = row.original.image;
          return imageUrl ? (
            <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary">
              <i className="fas fa-prescription-bottle"></i>
            </a>
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },

      {
        header: "Amount",
        accessorKey: "totalamount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="">{formatCurrency(row.original.totalamount)}</div>
            {/* <small className="text-muted">
              Items: {row.original.totalquantity}
            </small> */}
          </div>
        ),
      },
      {
        header: "Payment",
        accessorKey: "paymentmethod",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            {/* <div>{getPaymentMethodBadge(row.original.paymentmethod)}</div> */}
            <small className={`badge mt-1 ${row.original.paymentstatus === 'paid' ? 'bg-success' : 'bg-danger'}`}>
              {row.original.paymentstatus}
            </small>
          </div>
        ),
      },
      {
        header: "Status",
        accessorKey: "orderstatus",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => <OrderStatusBadge status={row.original.orderstatus} />,
      },
      {
        header: "Address",
        accessorKey: "address",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <small className="d-block text-truncate" style={{ maxWidth: "150px" }}>
              {row.original.address}
            </small>
            {/* <span className={`badge ${row.original.insidedhaka ? 'bg-success' : 'bg-secondary'}`}>
              {row.original.insidedhaka ? 'Inside Dhaka' : 'Outside Dhaka'}
            </span> */}
          </div>
        ),
      },
      {
        header: "Invoice",
        accessorKey: "",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <button
              onClick={() => handleDownloadInvoice(row.original)}
              className="btn btn-primary btn-sm"
              title="Download Invoice"
              disabled={generatingInvoice}
            >
              <i className={`fas ${generatingInvoice ? 'fa-spinner fa-spin' : 'fa-file-pdf'}`} style={{ width: "16px" }}></i>
            </button>
          </div>
        ),
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-1">
              {/* <button
                onClick={() => handleShowDetails(row.original)}
                className="btn btn-info btn-sm"
                title="View Details"
              >
                <i className="fas fa-eye"></i>
              </button> */}

              <Link to={`/order/${row.original.id}`} className="btn btn-info btn-sm">
                <i className="fas fa-eye"></i>
              </Link>

              <button
                onClick={() => handleUpdateStatus(row.original)}
                className="btn btn-warning btn-sm"
                title="Update Status"
              >
                <i className="fas fa-edit"></i>
              </button>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => showDeleteModal(row.original.id)}
                title="Delete Order"
                disabled={deletingOrder}
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>
          </div>
        ),
      },
    ],
    [deletingOrder]
  );

  // Calculate statistics
  const totalOrders = orders.length;
  const totalAmount = orders.reduce((sum, order) => sum + parseFloat(order.totalamount || 0), 0);
  const pendingOrders = orders.filter(order =>
    order.orderstatus === 'submitted' || order.orderstatus === 'pending'
  ).length;
  const cancelledOrders = orders.filter(order => order.orderstatus === 'cancelled').length;


  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  // Add function to handle invoice download
  const handleDownloadInvoice = async (order) => {
    setGeneratingInvoice(true);
    try {
      await generateOrderInvoicePDF(order);
      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Error generating invoice:", error);
      toast.error("Failed to generate invoice. Please try again.");
    } finally {
      setGeneratingInvoice(false);
    }
  };

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Orders" breadcrumbItem="All Orders" />

        <div className="mb-3 gap-3 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-3 align-items-center">
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0">Status:</label>
              <select
                className="form-select"
                style={{ width: "auto" }}
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="submitted">Submitted</option>
                <option value="Delivered">Delivered</option>
                {/* <option value="processing">Processing</option> */}
                {/* <option value="completed">Completed</option> */}
                <option value="Cancelled">Cancelled</option>
                {/* <option value="pending">Pending</option> */}
              </select>
            </div>

            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0">Payment:</label>
              <select
                className="form-select"
                style={{ width: "auto" }}
                value={paymentFilter}
                onChange={e => setPaymentFilter(e.target.value)}
              >
                <option value="">All Payments</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>

            <button
              className="btn btn-light"
              onClick={() => {
                setStatusFilter("");
                setPaymentFilter("");
              }}
            >
              <i className="fas fa-times me-1"></i> Clear Filters
            </button>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-success"
              onClick={() => fetchOrders(false)}
              disabled={refreshing}
              title="Refresh Orders"
            >
              <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
            </button>
          </div>
        </div>



        <TableContainer
          columns={columns}
          data={filteredOrders}
          isGlobalFilter={true}
          isPagination={true}
          SearchPlaceholder="Search orders, customers, phone..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          isCustomPageSize={true}
        />

        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteOrder}
          loader={deletingOrder}
          onCloseClick={() => setDeleteModal(false)}
          title="Delete Order"
          message="Are you sure you want to delete this order? This action cannot be undone."
        />

        {selectedOrder && (
          <>
            <OrderDetailsModal
              isOpen={detailsModalOpen}
              toggle={() => setDetailsModalOpen(false)}
              orderId={selectedOrder.id}
            />

            <UpdateOrderStatusModal
              isOpen={statusModalOpen}
              toggle={() => setStatusModalOpen(false)}
              order={selectedOrder}
              onUpdateSuccess={handleStatusUpdateSuccess}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AllOrders;